import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { Organization, OrgAccess, Website } from "@/types";
import { addOrganizations, updateOrganizationSettings } from "../slice";
import { RootState } from "@reduxjs/toolkit/dist/query/core/apiState";
import { OrganizationSettingsForm } from "@/pages/organization-settings/OrganizationSettings";
import { ApiError } from "@/types/api";

export const organizationsApi = createApi({
  reducerPath: "userOrganizations",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["userOrganizations"],
  endpoints: (builder) => ({
    fetchUserOrganizations: builder.query<Organization[], OrgAccess[] | SkipToken>({
      queryFn: async (userAccess, { dispatch }) => {
        const TASK = "Comercios del usuario";
        const organizations: Organization[] = [];
        const userOrganizationsIds = userAccess.map(({ id }) => id);

        try {
          dispatch(addLoadingTask(TASK));

          for await (const organizationId of userOrganizationsIds) {
            const docRef = doc(db, "organizations", organizationId);
            const snapshot = await getDoc(docRef);

            if (snapshot.exists()) {
              organizations.push({
                ...snapshot.data(),
                id: snapshot.id,
              } as Organization);
            }
          }

          dispatch(addOrganizations(organizations));

          return {
            data: organizations,
          };
        } catch (error) {
          return {
            error: {
              status: 400,
              statusText: "Error al cargar perfiles de usuario",
            },
          };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      providesTags: ["userOrganizations"],
    }),
    fetchOrganizationsByAlias: builder.query({
      queryFn: async (organizationAlias: string | SkipToken) => {
        try {
          const docRef = doc(db, `aliases/${String(organizationAlias)}`);
          const snapshot = await getDoc(docRef);

          if (snapshot.exists()) {
            return { data: { ...snapshot.data(), id: snapshot.id } };
          } else {
            return {
              error: {
                status: 404,
                statusText: "Organización no encontrada",
              },
            };
          }
        } catch (error) {
          return { error };
        }
      },
    }),
    fetchOrganizationsById: builder.query<Organization, string | SkipToken>({
      queryFn: async (organizationId, { dispatch }) => {
        const docRef = doc(db, `organizations/${String(organizationId)}`);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          const organization = { ...snapshot.data(), id: snapshot.id } as Organization;
          dispatch(addOrganizations([organization]));

          return {
            data: organization,
          };
        }

        return {
          error: {
            status: 404,
            statusText: "Comercio no encontrado",
          },
        };
      },
    }),
    fetchOrganizationWebsiteById: builder.query<Website, { organizationId: string | SkipToken; websiteId: string | SkipToken }>({
      queryFn: async ({ organizationId, websiteId }) => {
        const docRef = doc(db, `organizations/${String(organizationId)}/websites/${String(websiteId)}`);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          return {
            data: {
              ...snapshot.data(),
              id: snapshot.id,
            },
          };
        }

        return {
          error: {
            status: 404,
            statusText: "Sitio web no encontrado",
          },
        };
      },
    }),
    updateOrganizationSettings: builder.mutation<void, { organizationId: string; payload: OrganizationSettingsForm }>({
      queryFn: async ({ organizationId, payload }, { getState, dispatch }) => {
        try {
          const { auth } = getState() as RootState;

          if (!auth?.user?.uid) {
            return {
              error: {
                status: 401,
                statusText: "No autorizado",
              },
            };
          }

          const draft = {
            ...payload,
            lastUpdatedBy: auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          const collectionRef = doc(db, `organizations/${organizationId}`);
          await updateDoc(collectionRef, draft);
          dispatch(updateOrganizationSettings({ organizationId, settings: payload?.settings ?? {} }));
        } catch (error) {
          return {
            error: {
              status: 500,
              statusText: "Error al actualizar la organización",
            },
          };
        }
      },
    }),
  }),
});

export const {
  useFetchUserOrganizationsQuery,
  useUpdateOrganizationSettingsMutation,
  useFetchOrganizationsByAliasQuery,
  useFetchOrganizationsByIdQuery,
  useFetchOrganizationWebsiteByIdQuery,
} = organizationsApi;
